import {
  LoadingConstants,
  RentalManagerUserManagementCompanyWebApiHandler_getPendingUserProperties,
  useRentalManagerUser,
} from '@zg-rentals/rental-manager-env';
import React from 'react';
import { UserManagementStateContext } from '../providers/UserManagementStateProvider';
import type { UserManagementUserProfileDto } from '@zg-rentals/ts-user-management-client';
import { UmcCompanyOverviewWebApiHandler_getUserManagementPendingProfiles } from '@zg-rentals/ts-user-management-client';
import type { UserPropertyData, UserState } from './userStates';

export const usePendingUsersInternal = (): UserState => {
  const { user } = useRentalManagerUser();
  const [loadingState, setLoadingState] = React.useState<LoadingConstants>(LoadingConstants.INITIAL);
  const [profiles, setProfiles] = React.useState<Array<UserManagementUserProfileDto>>([]);
  const [propertyData, setPropertyData] = React.useState<Record<string, UserPropertyData>>({});
  const [propertyDataLoadingState, setPropertyDataLoadingState] = React.useState<LoadingConstants>(
    LoadingConstants.INITIAL,
  );

  const getPendingUsers = async () => {
    if (!user.userManagementCompanyId) {
      return;
    }
    let hasMoreData = true;
    let startEmail: string | undefined = undefined;
    const profiles: Array<UserManagementUserProfileDto> = [];
    try {
      while (hasMoreData) {
        const response = await UmcCompanyOverviewWebApiHandler_getUserManagementPendingProfiles({ startEmail });
        hasMoreData = response.hasMoreData;
        if (response.profiles.length > 0) {
          startEmail = response.profiles[response.profiles.length - 1].email;
          profiles.push(...response.profiles);
        }
      }
    } catch (error) {
      setLoadingState(LoadingConstants.ERROR);
      setProfiles([]);
      return;
    }
    setProfiles(profiles);
    setLoadingState(LoadingConstants.SUCCESS);

    setPropertyDataLoadingState(LoadingConstants.LOADING);
    try {
      await Promise.all(
        profiles.map(async (profile) => {
          const userId = profile.userToken || profile.email;
          const result = await RentalManagerUserManagementCompanyWebApiHandler_getPendingUserProperties({
            email: profile.email,
          });
          const properties = result.properties;
          setPropertyData((prev) => ({
            ...prev,
            [userId]: {
              propertyCount: properties.length,
              userId,
              loadingState: LoadingConstants.SUCCESS,
              olympusTier3ListingCount: 0,
            },
          }));
        }),
      );
      setPropertyDataLoadingState(LoadingConstants.SUCCESS);
    } catch (error) {
      setPropertyDataLoadingState(LoadingConstants.ERROR);
      return;
    }
  };

  React.useEffect(() => {
    getPendingUsers();
  }, [user.userManagementCompanyId]);

  const state = React.useMemo(() => {
    return {
      loadingState,
      profiles,
      propertyData,
      propertyDataLoadingState,
    };
  }, [loadingState, profiles, propertyData, propertyDataLoadingState]);
  return state;
};

export const usePendingUsers = (): UserState => {
  const umcState = React.useContext(UserManagementStateContext);

  if (!umcState) throw new Error('State Not initialized');

  return React.useMemo(() => umcState.pendingUsersState, [umcState.pendingUsersState]);
};
